/**
 * Which desk a pointer is over, worked out from the desks' drawn geometry rather than from the
 * element the browser reports under the pointer.
 *
 * The artwork's own hit areas are axis-aligned rectangles far wider than the isometric desks
 * they stand for, so neighbouring ones overlap and whichever comes later in the markup takes the
 * click — aiming at 20 picked 19. Upstairs, monitors and plants are painted over the desk tops
 * and the number badges sit in separate groups, so "what is under the pointer" is not a desk at
 * all. `seat-map.css` switches those rectangles off; this is the one place both the selection and
 * the click-to-zoom ask instead.
 *
 * A number badge wins outright, since it is what people aim at; otherwise the desk whose outline
 * (its top, its edges and its legs) contains the point, the front one where two overlap, as the
 * front one is painted over the other.
 */

type Point = readonly [number, number];

/** Only the visible rotation is hit-tested; the other three are display:none. */
function visibleView(svg: SVGSVGElement): Element | null {
  for (let i = 0; i < 4; i += 1) {
    const view = svg.querySelector<SVGGElement>(`#view-${i}`);
    if (view && view.style.display !== 'none') return view;
  }
  return svg;
}

/** A badge's chip in its own user space. Its ends are rounded to `radius`. */
export interface Chip {
  cx: number;
  cy: number;
  halfWidth: number;
  halfHeight: number;
  radius: number;
}

/**
 * The chip a badge's `.badge-face` draws: a circle for a bare number, or a pill once the label
 * is too long for one. Read from attributes rather than measured, so it works in the hidden views.
 */
export function chipOf(face: Element): Chip | null {
  if (face instanceof SVGCircleElement) {
    const r = face.r.baseVal.value;
    return {
      cx: face.cx.baseVal.value,
      cy: face.cy.baseVal.value,
      halfWidth: r,
      halfHeight: r,
      radius: r,
    };
  }
  if (face instanceof SVGRectElement) {
    const halfWidth = face.width.baseVal.value / 2;
    const halfHeight = face.height.baseVal.value / 2;
    return {
      cx: face.x.baseVal.value + halfWidth,
      cy: face.y.baseVal.value + halfHeight,
      halfWidth,
      halfHeight,
      radius: Math.min(face.rx.baseVal.value, halfWidth, halfHeight),
    };
  }
  return null;
}

/** Whether a point lies on a chip, its rounded ends included. */
function onChip([x, y]: Point, chip: Chip): boolean {
  const dx = Math.max(Math.abs(x - chip.cx) - (chip.halfWidth - chip.radius), 0);
  const dy = Math.max(Math.abs(y - chip.cy) - (chip.halfHeight - chip.radius), 0);
  return Math.hypot(dx, dy) <= chip.radius;
}

/** The pointer in an element's own user space, through every transform above it. */
function toLocal(el: SVGGraphicsElement, clientX: number, clientY: number): Point | null {
  const ctm = el.getScreenCTM();
  if (!ctm) return null;
  const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  return [p.x, p.y];
}

function pointsOf(el: Element): Point[] {
  if (el instanceof SVGLineElement) {
    return [
      [el.x1.baseVal.value, el.y1.baseVal.value],
      [el.x2.baseVal.value, el.y2.baseVal.value],
    ];
  }
  if (el instanceof SVGPolygonElement) {
    return Array.from(el.points, (p) => [p.x, p.y] as const);
  }
  return [];
}

/** Andrew's monotone chain; the outline of a desk's top, edges and legs together. */
function hull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;
  const cross = (o: Point, a: Point, b: Point) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (pts: Point[]) => {
    const out: Point[] = [];
    for (const p of pts) {
      while (out.length >= 2 && cross(out[out.length - 2]!, out[out.length - 1]!, p) <= 0) {
        out.pop();
      }
      out.push(p);
    }
    out.pop();
    return out;
  };
  return [...half(sorted), ...half(sorted.reverse())];
}

function inside([x, y]: Point, polygon: Point[]): boolean {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** Outlines are cached per desk group; the artwork's geometry never changes once injected. */
const outlines = new WeakMap<Element, Point[]>();

function outlineOf(desk: Element): Point[] {
  let outline = outlines.get(desk);
  if (!outline) {
    const parts = desk.querySelectorAll(':scope > .top, :scope > .edge, :scope > line');
    outline = hull(Array.from(parts).flatMap(pointsOf));
    outlines.set(desk, outline);
  }
  return outline;
}

/** The desk id under a pointer, or null over the floor, a wall or the furniture. */
export function deskAt(svg: SVGSVGElement, clientX: number, clientY: number): string | null {
  const view = visibleView(svg);
  if (!view) return null;
  // Copies laid over a seated character carry no id, so only the real desks are matched.
  const desks = Array.from(view.querySelectorAll<SVGGElement>('.desk[data-desk-id]'));

  let badge: { id: string; distance: number } | null = null;
  for (const desk of desks) {
    const face = desk.querySelector<SVGGraphicsElement>('.badge .badge-face');
    // A booked desk's badge is hidden behind the person sat at it.
    if (!face || face.closest<SVGElement>('.badge')?.style.display === 'none') continue;
    const chip = chipOf(face);
    const p = toLocal(face, clientX, clientY);
    if (!chip || !p) continue;
    const distance = Math.hypot(p[0] - chip.cx, p[1] - chip.cy);
    if (onChip(p, chip) && (!badge || distance < badge.distance)) {
      badge = { id: desk.getAttribute('data-desk-id') ?? '', distance };
    }
  }
  if (badge?.id) return badge.id;

  // Markup order is paint order, back to front, so the last desk that contains the point is the
  // one drawn over the rest.
  let front: string | null = null;
  for (const desk of desks) {
    const outline = outlineOf(desk);
    if (outline.length < 3) continue;
    const p = toLocal(desk, clientX, clientY);
    if (p && inside(p, outline)) front = desk.getAttribute('data-desk-id');
  }
  return front;
}
